import React, { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { loadWhatsAppTemplates, loadWhatsAppTemplateSample } from '../../lib/backOfficeApi';
import { toast } from '../../lib/buses';

// The ONE way this console picks a WhatsApp template and fills its parameters.
//
// It exists because "which template" is not a detail — for WhatsApp it IS the message.
// Meta only accepts free-form text inside the 24-hour customer-service window (i.e. to
// someone who messaged US in the last day). Every business-initiated send — a marketing
// blast, a reminder, a receipt — must name a template Meta has already approved. So a
// compose box that takes prose is not a simplification of this; it's a different thing
// that cannot be sent.
//
// Two surfaces need it and had diverged: Chat (send one template to one open thread) had
// a working picker, while the campaign wizard had a free-text body whose content the
// blast executor ignored entirely. Same engine now, so a template improvement lands on
// both and neither can drift back into prose.

// A template's `components` array mixes free structure (header media, body text, per-button
// URL suffixes). The values that were filled in belong to whoever the LAST real send
// targeted — reusing them unedited would attach a stranger's ticket PDF / order id to this
// send. So every leaf value (any `type: 'text'` param, or a header media `link`) is surfaced
// as an editable field; the button/header STRUCTURE (which slot exists, url vs quick_reply)
// stays exactly as Meta approved it, since only the values are ours to set.
export const templateEditableFields = (components) => {
  const fields = [];
  (components || []).forEach((comp, ci) => {
    (comp.parameters || []).forEach((p, pi) => {
      const label = (kind) => {
        const name = p.parameter_name ? p.parameter_name.replace(/_/g, ' ') : null;
        if (name) return name;
        if (comp.type === 'button') return `Button ${Number(comp.index ?? 0) + 1} · ${kind}`;
        return `${comp.type[0].toUpperCase()}${comp.type.slice(1)} · ${kind}`;
      };
      if (p.type === 'text') fields.push({ path: [ci, 'parameters', pi, 'text'], label: label('text'), value: p.text || '' });
      else if (p.type === 'document' && p.document) fields.push({ path: [ci, 'parameters', pi, 'document', 'link'], label: label('document link'), value: p.document.link || '' });
      else if (p.type === 'image' && p.image) fields.push({ path: [ci, 'parameters', pi, 'image', 'link'], label: label('image link'), value: p.image.link || '' });
      else if (p.type === 'video' && p.video) fields.push({ path: [ci, 'parameters', pi, 'video', 'link'], label: label('video link'), value: p.video.link || '' });
    });
  });
  return fields;
};

export const applyFieldValue = (components, path, value) => {
  const next = JSON.parse(JSON.stringify(components));
  let node = next;
  for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
  node[path[path.length - 1]] = value;
  return next;
};

// Placeholders the CAMPAIGN executor substitutes per recipient
// (campaignRoutes.processComponentVariables + EventContext). Listed so an operator writing
// a blast knows which values vary per person and which are literal — the difference between
// one message to five thousand people and five thousand personalised ones.
export const CAMPAIGN_VARIABLES = [
  '{name}', '{firstName}', '{phone}', '{email}',
  '{event_name}', '{event_date}', '{event_venue}', '{event_url}', '{ticket_url}', '{accreditation_url}',
];

// Templates that must NOT be offered for a bulk blast.
//
// `otp` is an AUTHENTICATION template — WhatsAppService.sendOtpTemplate uses it to deliver
// login/signup codes, and it is the one template in the catalogue the codebase itself
// identifies by category. Sending it to a marketing list would push a login-code message to
// people who never asked to sign in. The rest of the catalogue carries no category at all
// (see the open gap in KNOWN_TEMPLATES), so this deliberately names only what is KNOWN
// rather than guessing a category for twelve templates and filtering on a guess.
const BLAST_EXCLUDED = {
  otp: 'This is an authentication template — it delivers login codes. Sending it to a list would push a sign-in prompt to people who never asked for one.',
};

/**
 * value:    { name, languageCode, components } | null
 * onChange: (next | null) => void
 * variables: show the per-recipient placeholder hint (campaign mode) — off for a 1:1 send,
 *            where nothing is substituted and a placeholder would go out as literal text.
 */
export const WhatsAppTemplatePicker = ({ value, onChange, variables = false }) => {
  const [templates, setTemplates] = useState({ status: 'loading', rows: [] });
  const [loadingSample, setLoadingSample] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadWhatsAppTemplates()
      .then((rows) => { if (!cancelled) setTemplates({ status: 'ready', rows }); })
      .catch((e) => { if (!cancelled) { setTemplates({ status: 'error', rows: [] }); toast('Could not load templates', 'error', e?.message); } });
    return () => { cancelled = true; };
  }, []);

  const pick = (t) => {
    onChange({ ...t, components: [] });
    setLoadingSample(true);
    loadWhatsAppTemplateSample(t.name, t.languageCode)
      .then((r) => { setLoadingSample(false); onChange({ ...t, components: r.components, lastUsedAt: r.lastUsedAt }); })
      .catch((e) => { setLoadingSample(false); toast('Could not load template sample', 'error', e?.message); });
  };

  if (!value) {
    return (
      <div>
        {templates.status === 'loading' && <div className="py-8 text-center"><Loader2 className="w-4 h-4 text-[#9CA3AF] animate-spin inline-block" /></div>}
        {templates.status === 'error' && <div className="py-6 text-center text-[11.5px] text-[#9B1F1F]">Couldn't load templates</div>}
        {templates.status === 'ready' && templates.rows.length === 0 && <div className="py-6 text-center text-[11.5px] text-[#9CA3AF]">No approved templates configured for this number.</div>}
        <div className="space-y-2 max-h-[280px] overflow-auto">
          {templates.rows.map((t) => {
            // `variables` is only set by the campaign wizard, so this gate applies to a BLAST
            // and never to a deliberate 1:1 send from Chat (where an operator resending a
            // login code to one person on the phone is a legitimate support action).
            const blocked = variables ? BLAST_EXCLUDED[t.name] : null;
            return (
              <button
                key={`${t.name}:${t.languageCode}`}
                type="button"
                disabled={!!blocked}
                title={blocked || undefined}
                onClick={() => pick(t)}
                className={`w-full text-left px-3.5 py-2.5 rounded-md border transition-colors ${blocked
                  ? 'border-[#EEEDEA] bg-[#FBFAF7] cursor-not-allowed'
                  : 'border-[#E6E4E1] hover:border-[#1F2937] hover:bg-[#FBFAF7]'}`}
              >
                <span className={`text-[12.5px] font-semibold font-mono ${blocked ? 'text-[#9CA3AF]' : 'text-[#1F2937]'}`}>{t.name}</span>
                <div className="text-[10.5px] text-[#9CA3AF] mt-0.5">{blocked ? `${t.languageCode} · not available for a blast` : t.languageCode}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const fields = templateEditableFields(value.components);
  return (
    <div>
      <button type="button" onClick={() => onChange(null)} className="text-[11px] text-[#6B7280] hover:text-[#1F2937] mb-3">← Back to templates</button>
      <div className="text-[12.5px] font-semibold text-[#1F2937] font-mono mb-2">{value.name} <span className="text-[10.5px] font-normal text-[#9CA3AF]">{value.languageCode}</span></div>
      {loadingSample ? (
        <div className="py-8 text-center"><Loader2 className="w-4 h-4 text-[#9CA3AF] animate-spin inline-block" /></div>
      ) : (
        <>
          <div className="rounded-md bg-[#F2F1ED] px-3 py-2 mb-3.5 flex items-start gap-2">
            <FileText className="w-3.5 h-3.5 text-[#6B7280] flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-[#6B7280] leading-relaxed">
              Values below are copied from the last real send of this template — check every one before sending; a link or name meant for a different customer will go out unless you edit it.
              {variables && <> Use {CAMPAIGN_VARIABLES.slice(0, 4).join(' ')} or {CAMPAIGN_VARIABLES.slice(4).join(' ')} to fill per recipient; anything else goes out literally, identical to everyone.</>}
            </div>
          </div>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.path.join('.')}>
                <div className="text-[10.5px] font-medium text-[#6B7280] mb-1 capitalize">{f.label}</div>
                <input
                  value={f.value}
                  onChange={(e) => onChange({ ...value, components: applyFieldValue(value.components, f.path, e.target.value) })}
                  className="w-full h-8 px-2.5 text-[12px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937]"
                />
              </div>
            ))}
            {fields.length === 0 && <div className="text-[11.5px] text-[#9CA3AF]">This template has no fillable parameters — it sends exactly as approved.</div>}
          </div>
        </>
      )}
    </div>
  );
};
